import { CAST_AI } from '@cast/constants';
import {
  Column,
  ExternalLink,
  TableHeader,
  makeActionsColumn,
  Box,
} from '@cast/design-system';
import { useRegionsQuery } from '@cast/shared-inventory-data-access';

import { CopyToClipboard } from 'components/clickable-actions';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { useAbility } from '@cast/shared-ability-data-access';
import { makeNodeListLink } from 'features/cluster/nodes';

import { makeSelectionTemplatesLink } from '../../utils';
import { EdgeLocationActions } from '../_components/EdgeLocationActions';
import { EdgeLocationStateChipWithWarning } from '../_components/EdgeLocationStateChip';
import { useEdgeLocationDetailsDrawer } from '../_hooks';
import { CreateEdgeLocationButton } from '../components/CreateEdgeLocationButton';
import { CUSTOM_EDGE_PROVIDER, EDGE_PROVIDER_NAME_MAP } from '../constants';
import { EnhancedEdgeLocation } from '../types';
import { getRegionDisplayName } from '../utils';
import { NodeTemplatesCell } from './NodeTemplatesCell';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { CspIcon } from '@cast/shared-csp-icons-ui';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

type EdgeLocationsTableProps = {
  showFullTable?: boolean;
};

export const EdgeLocationsTable = ({
  showFullTable = true,
}: EdgeLocationsTableProps) => {
  const [canEdit, canDelete] = useAbility().canMany([
    ['update', 'ClusterEdgeLocations'],
    ['delete', 'ClusterEdgeLocations'],
  ]);

  const showActions = showFullTable && (canEdit || canDelete);
  const { cluster } = useCluster();
  const { openEdgeLocationDetailsDrawer } = useEdgeLocationDetailsDrawer();
  const { regions } = useRegionsQuery();
  const nodeTemplatesLink = makeSelectionTemplatesLink({
    clusterId: cluster.id,
  });

  return (
    <SearchableTable<EnhancedEdgeLocation>
      testId="edge-locations-table"
      urlKey="edge-locations-list"
      cacheKey={tableCacheKeys.EDGE_LOCATIONS}
      fillParentHeight
      components={{
        noData: (
          <NoData
            entity="edge location"
            actions={showFullTable ? <CreateEdgeLocationButton /> : undefined}
          />
        ),
        noResults: <NoResults entity="edge location" />,
        failed: <FailedToLoad title="Failed to load edge locations" />,
      }}
      outerHeader={
        <TableHeader
          recordsCount={<SearchableTableRecordsCount title="edge location" />}
          actions={
            showFullTable ? (
              <CreateEdgeLocationButton variant="text" />
            ) : undefined
          }
          showSettings={showFullTable}
          sx={{ px: !showFullTable ? 32 : 0 }}
        />
      }
      onRowClick={
        showFullTable
          ? (_, { row }) => openEdgeLocationDetailsDrawer({ edgeLocation: row })
          : undefined
      }
    >
      {showFullTable && (
        <Column<EnhancedEdgeLocation>
          id="id"
          header="id"
          width={50}
          renderCell={({ id }) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CopyToClipboard text={id!} iconSize="20px" />
            </Box>
          )}
        />
      )}
      <Column<EnhancedEdgeLocation>
        id="name"
        header="Location name"
        minWidth={80}
        sortable
        cellWrapperSx={{ pl: !showFullTable ? 32 : 0 }}
      />
      {showFullTable && (
        <Column<EnhancedEdgeLocation>
          id="description"
          header="Description"
          minWidth={100}
          renderCell={({ description }) => description || '-'}
        />
      )}
      <Column<EnhancedEdgeLocation>
        id="provider"
        header="Provider"
        minWidth={showFullTable ? 50 : 60}
        sortable
        accessor="provider"
        renderCell={({ provider }) => (
          <Box
            data-sort-value={provider}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {provider === CUSTOM_EDGE_PROVIDER ? (
              EDGE_PROVIDER_NAME_MAP.custom
            ) : (
              <CspIcon csp={provider} size={24} />
            )}
          </Box>
        )}
      />
      <Column<EnhancedEdgeLocation>
        id="region"
        header="Region"
        minWidth={60}
        cellWrapperSx={{ px: showFullTable ? 16 : 4 }}
        sortable
        renderCell={({ region }) => getRegionDisplayName(region, regions)}
      />
      <Column<EnhancedEdgeLocation>
        id="node-templates"
        header="Node templates"
        minWidth={showFullTable ? 120 : 80}
        cellWrapperSx={{ px: showFullTable ? 16 : 4 }}
        renderCell={(row) => (
          <NodeTemplatesCell
            edgeLocation={row}
            nodeTemplatesLink={nodeTemplatesLink}
          />
        )}
      />
      <Column<EnhancedEdgeLocation>
        id="edge-nodes"
        header="Nodes"
        minWidth={showFullTable ? 70 : 50}
        cellWrapperSx={{ px: showFullTable ? 16 : 4 }}
        infoTooltip={`A virtual machine provisioned by ${CAST_AI} via Omni that has a single-node K3s
cluster running on it.`}
        infoTooltipProps={{
          placement: 'bottom-end',
        }}
        sortable
        sortingType="number"
        accessor="totalEdgeCount"
        renderCell={({ name, totalEdgeCount }) =>
          name && totalEdgeCount && totalEdgeCount > 0 ? (
            <ExternalLink
              data-sort-value={totalEdgeCount}
              link={makeNodeListLink(cluster.id, {
                orgId: cluster.organizationId!,
                labels: [`omni.cast.ai/edge-location-name=${name}`],
              })}
              sx={{ typography: 'P12M' }}
              onClick={(e) => e.stopPropagation()}
            >
              {totalEdgeCount}
            </ExternalLink>
          ) : (
            '-'
          )
        }
      />
      <Column<EnhancedEdgeLocation>
        id="state"
        header="State"
        minWidth={70}
        sortable
        cellWrapperSx={{ pr: !showFullTable ? 32 : 0 }}
        accessor="mappedState"
        cellSx={{ overflow: 'visible' }}
        renderCell={(row) => (
          <EdgeLocationStateChipWithWarning
            edgeLocation={row}
            clusterId={cluster.id}
            includeTooltip
          />
        )}
      />
      {showActions &&
        makeActionsColumn<EnhancedEdgeLocation>({
          action: (edgeLocation) => (
            <EdgeLocationActions edgeLocation={edgeLocation} />
          ),
        })}
    </SearchableTable>
  );
};
